import { FormikHelpers } from 'formik';
import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { mapPayloadToUpsertInput } from 'modules/organizer/domain/data-mappers/organizer-upsert.data-mapper';
import { OrganizerUpsertPayload } from 'modules/organizer/domain/interfaces/organizer-upsert-payload';
import { upsertOrganizer } from 'modules/organizer/store/organizer/actions';
import { OrganizerFormValues } from 'modules/organizer/ui/components/organizer-form';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Background, backgroundColors } from 'shared/constants/backgrounds';
import { useFetchLogoBackground } from 'shared/hooks/fetchering/use-fetch-logo-background';
import { useUpdateLogoBackground } from 'shared/hooks/mutations/use-update-logo-background';
import { useAppDispatch } from 'store/hooks';

export function useProfileForm() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isLoading: loading, info, isSaving } = useOrganizer();
  const { data: logoBackground, isFetching: isLogoBackgroundFetching } = useFetchLogoBackground();
  const { user } = useAuth();
  const { updateLogoBackground } = useUpdateLogoBackground();

  const initialValues = useMemo((): (OrganizerUpsertPayload & { backgroundColor?: Background }) | undefined => {
    if (!info) return undefined;
    return {
      id: info.id,
      name: info.name,
      sportType: info.sportType,
      email: info.email,
      phone: info.phone,
      description: info.description,
      logoUrl: info.logoUrl,
      address: info.address,
      backgroundColor: backgroundColors[logoBackground?.type ?? 0],
    };
  }, [info, logoBackground]);

  async function handleSubmit(data: OrganizerUpsertPayload, formikHelpers: FormikHelpers<OrganizerFormValues>) {
    const params = mapPayloadToUpsertInput(data, user!.id);
    const bgColor = data.logo?.[0]?.background;

    try {
      const [organizerResult] = await Promise.allSettled([
        dispatch(upsertOrganizer(params)).unwrap(),
        updateLogoBackground({ bgColor, tournamentId: info?.id ?? params.id }),
      ]);

      if (organizerResult.status === 'fulfilled') {
        toast.success(t('settings.saveSettingsSuccess'));
      } else {
        toast.error(t('settings.saveSettingsInternalError'));
      }
    } finally {
      formikHelpers.setSubmitting(false);
    }
  }

  return {
    initialValues,
    isLoading: loading || isLogoBackgroundFetching,
    isSaving,
    handleSubmit,
  };
}
